import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Progress, Tooltip } from "antd";
import { IoCall, IoClose, IoCheckmarkCircle, IoChevronForward, IoChevronDown, IoChevronUp } from "react-icons/io5";
import { FaDatabase, FaWhatsapp, FaUsers } from "react-icons/fa";
import { MdEmail, MdHub } from "react-icons/md";
import { checkContactsExist, checkLeadsExist } from "../../../core/data/redux/slices/SetupGuideSlice";
import { dismissSetupGuide } from "../../../core/data/redux/slices/ProfileSlice";
import "./GettingStartedWidget.css";

const LS_MINIMIZED = "voycell_setup_minimized";

const STEPS = [

  {
    id: "import_db",
    Icon: FaDatabase,
    iconBg: "rgba(255,162,1,0.18)",
    iconColor: "#FFA201",
    title: "Import Database",
    description: "Import contacts from CSV, Excel or Zoho",
    route: "/crm/import-and-export",
    fromSetup: "import_db",
  },
  {
    id: "import_leads",
    Icon: FaUsers,
    iconBg: "rgba(82,196,26,0.18)",
    iconColor: "#52c41a",
    title: "Import Leads",
    description: "Upload and manage your lead list",
    route: "/leads",
    fromSetup: "import_leads",
    extra: "openImport=1",
  },
  {
    id: "setup_whatsapp",
    Icon: FaWhatsapp,
    iconBg: "rgba(37,211,102,0.18)",
    iconColor: "#25D366",
    title: "Setup WhatsApp",
    description: "Connect your WhatsApp Business account",
    route: "/whatsapp/setting",
    fromSetup: "setup_whatsapp",
  },
  {
    id: "setup_email",
    Icon: MdEmail,
    iconBg: "rgba(255,77,79,0.18)",
    iconColor: "#ff7875",
    title: "Setup Email",
    description: "Configure Gmail, Outlook or SMTP sync",
    route: "/crm/sync-and-integration",
    fromSetup: "setup_email",
    extra: "highlight=email",
  },
  {
    id: "connect_crm",
    Icon: MdHub,
    iconBg: "rgba(114,46,209,0.18)",
    iconColor: "#b37feb",
    title: "Connect CRM",
    description: "Sync with HubSpot, Zoho or Pipedrive",
    route: "/crm/sync-and-integration",
    fromSetup: "connect_crm",
    extra: "highlight=crm",
  },
    {
    id: "speak_to_lead",
    Icon: IoCall,
    iconBg: "rgba(30,203,226,0.18)",
    iconColor: "#1ECBE2",
    title: "Speak to Lead",
    description: "Make your first call to a lead",
    route: "/agent/crm/call-logs-and-recordings",
    fromSetup: "speak_to_lead",
  },
];

const buildUrl = (step) => {
  const params = [`fromSetup=${step.fromSetup}`];
  if (step.extra) params.push(step.extra);
  return `${step.route}?${params.join("&")}`;
};

const GettingStartedWidget = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [minimized, setMinimized] = useState(() => localStorage.getItem(LS_MINIMIZED) === "true");
  const [completed, setCompleted] = useState([]);

  // Redux selectors
  const profile = useSelector((state) => state.profile);
  const callGraph = useSelector((state) => state.dashboardData?.callGraph);
  const contactsExist = useSelector((state) => state.setupGuide.contactsExist);
  const leadsExist    = useSelector((state) => state.setupGuide.leadsExist);

  // Dismissed state comes from profile (DB-backed, cross-device)
  const dismissed = profile?.setupGuideDismissed === true;

  // Fetch contacts and leads existence separately (each cached independently)
  useEffect(() => {
    if (dismissed) return;
    dispatch(checkContactsExist());
    dispatch(checkLeadsExist());
  }, [dismissed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recompute which steps are done whenever relevant data changes
  useEffect(() => {
    if (dismissed) return;

    const done = [];

    // 1. Email — any email account connected
    if (profile?.accounts?.email?.some((a) => a.isConnected)) done.push("setup_email");

    // 2. CRM — any CRM connected
    if (profile?.accounts?.crm?.some((a) => a.isConnected)) done.push("connect_crm");

    // 3. WhatsApp — WABA connected
    if (profile?.accounts?.whatsapp?.isConnected) done.push("setup_whatsapp");

    // 4. Calls — any call recorded in the last 30 days graph
    if (callGraph?.days?.some((d) => (d.inbound || 0) + (d.outbound || 0) > 0)) {
      done.push("speak_to_lead");
    }

    // 5. DB contacts (category: "contact") — drives import_db step only
    if (contactsExist === true) done.push("import_db");

    // 6. Leads (category: "lead") — drives import_leads step only
    if (leadsExist === true) done.push("import_leads");

    setCompleted(done);
  }, [profile, callGraph, contactsExist, leadsExist, dismissed]);

  // Hidden if dismissed from DB
  if (dismissed) return null;

  const percent = Math.round((completed.length / STEPS.length) * 100);
  const firstActiveIdx = STEPS.findIndex((s) => !completed.includes(s.id));

  const handleDismiss = (e) => {
    e.stopPropagation();
    // Persist to DB (syncs across devices) — Redux state updates optimistically via fulfilled case
    dispatch(dismissSetupGuide());
  };

  const handleMinimize = (e) => {
    e.stopPropagation();
    const next = !minimized;
    setMinimized(next);
    localStorage.setItem(LS_MINIMIZED, next ? "true" : "false");
  };

  const handleStepClick = (step) => {
    if (completed.includes(step.id)) return;
    navigate(buildUrl(step));
  };

  // All done → show a dismissible completion banner
  if (percent === 100) {
    return (
      <div className="gsw-wrapper gsw-wrapper--done">
        <div className="gsw-header">
          <div className="gsw-title-block">
            <span className="gsw-rocket">🎉</span>
            <div>
              <div className="gsw-title" style={{ color: "#52c41a" }}>
                You&apos;re all set! Setup complete.
              </div>
              <div className="gsw-subtitle">
                All 6 steps are done — your VOYCELL system is fully configured.
              </div>
            </div>
          </div>
          <div className="gsw-progress-block">
            <Progress
              percent={100}
              size={56}
              type="circle"
              strokeColor="#52c41a"
              trailColor="rgba(255,255,255,0.12)"
              strokeWidth={8}
              format={() => (
                <span style={{ color: "#52c41a", fontSize: 18 }}>✓</span>
              )}
            />
            <button className="gsw-close" onClick={handleDismiss} title="Dismiss">
              <IoClose size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gsw-wrapper">
      {/* Header */}
      <div className="gsw-header">
        <div className="gsw-title-block">
          <span className="gsw-rocket">🚀</span>
          <div>
            <div className="gsw-title">
              Welcome! Let&apos;s set up your calling system in just a few minutes
            </div>
            <div className="gsw-subtitle">
              Complete the steps below to unlock the full power of VOYCELL
            </div>
          </div>
        </div>

        <div className="gsw-progress-block">
          <Progress
            percent={percent}
            size={56}
            type="circle"
            strokeColor={{ "0%": "#FFA201", "100%": "#ff6b35" }}
            trailColor="rgba(255,255,255,0.12)"
            strokeWidth={8}
            format={(p) => (
              <span style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>
                {p}%
              </span>
            )}
          />
          <button className="gsw-minimize" onClick={handleMinimize} title={minimized ? "Expand" : "Collapse"}>
            {minimized ? <IoChevronDown size={15} /> : <IoChevronUp size={15} />}
          </button>
        </div>
      </div>

      {/* Collapsible section */}
      <div className={`gsw-collapsible${minimized ? " gsw-collapsible--hidden" : ""}`}>
        {/* Thin progress bar */}
        <div className="gsw-bar-track">
          <div className="gsw-bar-fill" style={{ width: `${percent}%` }} />
        </div>

        {/* Step cards */}
        <div className="gsw-cards">
        {STEPS.map((step, idx) => {
          const isDone = completed.includes(step.id);
          const activeRank = STEPS.slice(0, idx + 1).filter((s) => !completed.includes(s.id)).length;
          const isActive = !isDone && idx === firstActiveIdx;
          const { Icon } = step;

          return (
            <Tooltip
              key={step.id}
              title={isDone ? "Completed ✓" : step.description}
              placement="bottom"
            >
              <div
                className={`gsw-card${isDone ? " gsw-card--done" : ""}${isActive ? " gsw-card--active" : ""}`}
                onClick={() => handleStepClick(step)}
                role={isDone ? undefined : "button"}
              >
                {isDone && (
                  <div className="gsw-check">
                    <IoCheckmarkCircle size={20} color="#52c41a" />
                  </div>
                )}

                <div
                  className="gsw-icon"
                  style={{ background: step.iconBg, color: step.iconColor }}
                >
                  <Icon size={20} />
                </div>

                <div className="gsw-card-title">{step.title}</div>
                <div className="gsw-card-desc">{step.description}</div>

                <div className="gsw-card-footer">
                  {isActive && (
                    <span className="gsw-badge">UP NEXT</span>
                  )}
                  {!isDone && !isActive && (
                    <span className="gsw-step-num">Step {activeRank}</span>
                  )}
                  {!isDone && (
                    <IoChevronForward size={14} className="gsw-arrow" />
                  )}
                </div>
              </div>
            </Tooltip>
          );
        })}
        </div>
      </div>{/* end gsw-collapsible */}
    </div>
  );
};

export default GettingStartedWidget;
